import React, { useState, useEffect, useCallback } from "react";
import {
  Target,
  BarChart3,
  AlertTriangle,
  TrendingUp,
  Shield,
  Download,
  RefreshCw,
  CheckCircle,
  XCircle,
  AlertCircle,
  Activity,
  Brain,
  FileText,
  History,
  Users,
  X,
  Tag,
  Table,
  Calculator,
  Edit,
  Play,
  Plus,
  CheckCircle2,
} from "lucide-react";
import { useProject } from "../contexts/ProjectContext";
import { riskMappingAPI } from "../services/riskMappingAPI";
import api from "../api";
import IntelligentControlGapsAnalysis from "../components/RiskMapping/IntelligentControlGapsAnalysis";
import AIRiskScoringEngine from "../components/RiskMapping/AIRiskScoringEngine";
import AIAnalyticsRecommendations from "../components/RiskMapping/AIAnalyticsRecommendations";
import HelpTooltip from "../components/HelpTooltip";
import { EnhancedRiskControlMappingTable } from "../components/RCSA/EnhancedRiskControlMappingTable";
import { MappingStats } from "../components/RCSA/MappingStats";
import { MappingFilters } from "../components/RCSA/MappingFilters";
import ProcessStepRiskControlService from "../services/ProcessStepRiskControlService";
import RiskAssessmentTable from "../components/RiskMapping/RiskAssessmentTable";
import { ResidualRiskCalculator } from "../components/RCSA/ResidualRiskCalculator";
import { ControlsOverview } from "../components/RCSA/ControlsOverview";
import { AddControlModal } from "../components/RCSA/AddControlModal";
import RiskTemplateQCTab from "../components/RiskMapping/RiskTemplateQCTab";
import TemplateImportTab from "../components/RiskMapping/TemplateImportTab";
import toastService from "../services/toastService";

interface RiskMetrics {
  overall_risk_score: number;
  control_coverage: number;
  automation_ratio: number;
  compliance_score: number;
  high_risk_processes: number;
  critical_findings: number;
  total_processes: number;
}

interface ProcessStepMapping {
  processStep: {
    id: number;
    processStepId: string;
    stepNumber: number;
    processStepName: string;
    description: string;
    processStepType: string;
    frequency: string;
    volume: string;
    performingCountry: string;
    legalEntity: string;
    supportingModel: string;
    thirdPartyBusinessType: string;
  };
  riskControlMappings: Array<{
    processStepId: number;
    processStepName: string;
    processStepType: string;
    processStepDescription: string;
    stepNumber: number;
    frequency: string;
    volume: string;
    performingCountry: string;
    legalEntity: string;
    supportingModel: string;
    thirdPartyBusinessType: string;
    // Risk fields
    riskId: string | null;
    riskName: string | null;
    riskDescription: string | null;
    riskTaxonomyPath: string | null;
    inherentLikelihood: number | null;
    inherentImpact: number | null;
    inherentRiskScore: number | null;
    inherentRiskLevel: string | null;
    timeframe: string | null;
    // Control fields
    controlId: string | null;
    controlName: string | null;
    controlDescription: string | null;
    controlType: string | null;
    controlOwner: string | null;
    controlExecutor: string | null;
    controlStatus: string | null;
    controlFrequency: string | null;
    designEffectiveness: string | null;
    operatingEffectiveness: string | null;
    overallEffectiveness: string | null;
    // Status
    mappingStatus: "COMPLETE" | "INCOMPLETE" | "NEEDS_RISKS" | "NEEDS_CONTROLS";
    needsRiskMapping: boolean;
    needsControlMapping: boolean;
  }>;
  summary: {
    totalRisks: number;
    totalControls: number;
    totalMappings: number;
    completionStatus: string;
    needsAttention: boolean;
  };
}

interface ProjectStats {
  totalProcessSteps: number;
  totalRisks: number;
  totalControls: number;
  totalMappings: number;
  completeSteps: number;
  incompleteSteps: number;
}

const RiskMapping: React.FC = () => {
  const { currentProject } = useProject();
  const [activeTab, setActiveTab] = useState("Risk Assessment Table");
  const [viewMode, setViewMode] = useState("Consultant");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Data states
  const [processSteps, setProcessSteps] = useState<any[]>([]);
  const [processStepsLoaded, setProcessStepsLoaded] = useState(false);
  const [analysisResults, setAnalysisResults] = useState<any>(null);

  // Risk mapping specific states
  const [selectedProcessStep, setSelectedProcessStep] = useState<
    string | undefined
  >(undefined);
  const [riskLevel, setRiskLevel] = useState(2); // Default to Level 2: Tactical
  const [assessmentGenerated, setAssessmentGenerated] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [generationProgress, setGenerationProgress] = useState<string>("");

  // Analytics states
  const [analyticsData, setAnalyticsData] = useState<RiskMetrics>({
    overall_risk_score: 0,
    control_coverage: 0,
    automation_ratio: 0,
    compliance_score: 0,
    high_risk_processes: 0,
    critical_findings: 0,
    total_processes: 0,
  });

  // Risk & Control Mapping states
  const [mappingData, setMappingData] = useState<ProcessStepMapping[]>([]);
  const [projectStats, setProjectStats] = useState<ProjectStats | null>(null);
  const [mappingFilters, setMappingFilters] = useState({
    completionStatus: "all",
    stepType: "all",
    searchTerm: "",
  });
  const [showMappingFilters, setShowMappingFilters] = useState(false);
  const [showMappingStats, setShowMappingStats] = useState(true);
  const [savingMapping, setSavingMapping] = useState(false);

  // AI Insights states
  const [controlGapsInsights, setControlGapsInsights] = useState<any[]>([]);
  const [riskScoringInsights, setRiskScoringInsights] = useState<any>(null);
  const [analyticsInsights, setAnalyticsInsights] = useState<any>(null);

  // Additional states for missing functionality
  const [databaseCategories, setDatabaseCategories] = useState<any[]>([]);
  const [showClassificationPanel, setShowClassificationPanel] = useState(false);

  // Residual Risk Calculator state
  const [selectedRiskForCalculation, setSelectedRiskForCalculation] = useState<number | null>(null);

  // Controls Overview state
  const [controls, setControls] = useState<any[]>([]);
  const [loadingControls, setLoadingControls] = useState(false);
  const [selectedControl, setSelectedControl] = useState<any | null>(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [testingDialogOpen, setTestingDialogOpen] = useState(false);
  const [gapDialogOpen, setGapDialogOpen] = useState(false);
  const [addControlModalOpen, setAddControlModalOpen] = useState(false);

  const tabs = [
    { name: "Risk Assessment Table", icon: Table },
    { name: "Controls Overview", icon: Shield },
    { name: "Residual Risk Calculator", icon: Calculator },
    { name: "Template Quality Check", icon: CheckCircle2 },
    { name: "Template Import", icon: FileText },
  ];

  // Load process steps from process flow map - purely API-driven
  const loadProcessStepsFromFlowMap = useCallback(async () => {
    if (!currentProject?.id) {
      console.log("No project selected, skipping process steps load");
      setProcessSteps([]);
      setProcessStepsLoaded(false);
      return;
    }

    // Clear any existing error state
    setError(null);

    try {
      // Only endpoint: Process maps (actual flowchart data)
      // Load ONLY Level 3 (Operational) maps for risk assessment
      const processMapResponse = await api.get(
        `/api/process-maps/project/${currentProject.id}?level=3`
      );

      // If no process maps found, try alternative API endpoints
      if (!processMapResponse.data?.process_maps?.length) {
        console.log(
          "❌ No process maps found, trying alternative endpoints..."
        );

        // Try getting process steps directly
        try {
          const processStepsResponse = await api.get(
            `/api/projects/${currentProject.id}/process-steps`
          );
        

          if (processStepsResponse.data?.processSteps?.length > 0) {
            const steps = processStepsResponse.data.processSteps.map(
              (step: any, index: number) => ({
                id: step.id || `step-${index}`,
                name: step.name || step.processStepName || `Step ${index + 1}`,
                description:
                  step.description ||
                  `Process step: ${step.name || step.processStepName}`,
                order: index + 1,
                risk_level: step.riskLevel || "Medium",
                controls: [],
                category: "Process Step",
                process_id: step.processId || `PROC_${currentProject.id}`,
                process_step_id:
                  step.processStepId || step.id || `STEP_${index + 1}`,
                processId: step.processId || `PROC_${currentProject.id}`,
                stepId: step.processStepId || step.id || `STEP_${index + 1}`,
              })
            );

            console.log(
              "✅ Successfully loaded process steps from direct API:",
              steps.length
            );
            setProcessSteps(steps);
            setProcessStepsLoaded(true);
            return;
          }
        } catch (directApiError) {
          console.log(
            "❌ Direct process steps API also failed:",
            directApiError
          );
        }

        // Create sample process steps for testing
        console.log("🔧 Creating sample process steps for testing...");
        const sampleSteps = [
          {
            id: "sample-1",
            name: "Data Input & Validation",
            description: "Initial data entry and validation process",
            order: 1,
            risk_level: "Medium",
            controls: [],
            category: "Process Step",
            process_id: `PROC_${currentProject.id}_SAMPLE`,
            process_step_id: "STEP_01",
            processId: `PROC_${currentProject.id}_SAMPLE`,
            stepId: "STEP_01",
          },
          {
            id: "sample-2",
            name: "Approval & Authorization",
            description: "Management approval and authorization step",
            order: 2,
            risk_level: "High",
            controls: [],
            category: "Process Step",
            process_id: `PROC_${currentProject.id}_SAMPLE`,
            process_step_id: "STEP_02",
            processId: `PROC_${currentProject.id}_SAMPLE`,
            stepId: "STEP_02",
          },
          {
            id: "sample-3",
            name: "Financial Processing",
            description: "Financial transaction processing and recording",
            order: 3,
            risk_level: "Critical",
            controls: [],
            category: "Process Step",
            process_id: `PROC_${currentProject.id}_SAMPLE`,
            process_step_id: "STEP_03",
            processId: `PROC_${currentProject.id}_SAMPLE`,
            stepId: "STEP_03",
          },
        ];

        console.log("✅ Using sample process steps for demonstration");
        setProcessSteps(sampleSteps);
        setProcessStepsLoaded(true);
        setError(
          "Using sample data - Please create actual process maps in Process Maps section"
        );
        return;
      }

      if (processMapResponse.data?.process_maps?.length > 0) {
        const processMaps = processMapResponse.data.process_maps;
        let allSteps: any[] = [];

        processMaps.forEach((map: any, mapIndex: number) => {
          console.log(
            `🗺️ Processing map ${mapIndex + 1}:`,
            map.title,
            "ID:",
            map.id
          );
          console.log("🗺️ Processing map ", map.process_id);
          const processIdPattern = `PROC_${
            currentProject.id
          }_L3_${map.title?.replace(/[^A-Z0-9]/g, "")}_${map.id}`;
          console.log(`🆔 Generated process ID pattern: ${processIdPattern}`);
          if (map.map_data) {
            let mapData = map.map_data;
            console.log("📋 Map data type:", typeof mapData);
            console.log("📋 Map data:", mapData);

            if (typeof mapData === "string") {
              try {
                mapData = JSON.parse(mapData);
              } catch (e) {
                console.warn("Failed to parse map_data for map:", map.id);
                return;
              }
            }

            let steps: any[] = [];

            // Extract from React Flow nodes (standard structure)
            if (mapData?.nodes && Array.isArray(mapData.nodes)) {
              console.log(`🔍 Found ${mapData.nodes.length} nodes in map data`);
              console.log(
                "📝 First 3 nodes:",
                mapData.nodes.slice(0, 3).map((n: any) => ({
                  id: n.id,
                  type: n.type,
                  label: n.data?.label,
                }))
              );

              const processNodes = mapData.nodes.filter((node: any) => {
                const isValid =
                  node.type !== "group" &&
                  !node.id?.includes("connector") &&
                  !node.id?.includes("group") &&
                  node.data?.label &&
                  node.data.label.trim() !== "";

                console.log(
                  `Node ${node.id}: type=${node.type}, label="${node.data?.label}", valid=${isValid}`
                );
                return isValid;
              });

              steps = processNodes.map((node: any, index: number) => {
                const processId = node.data?.process_id || map.process_id;
                const stepId = `${processId}_STEP_${String(index + 1).padStart(2, "0")}`;

                return {
                  id: `${map.id}-step-${index}`,
                  name: node.data.label,
                  description:
                    node.data?.description || `Process step: ${node.data.label}`,
                  order: allSteps.length + index + 1,
                  risk_level: node.data?.risk_level || "Medium",
                  controls: [],
                  category: node.data?.category || "Process",
                  // Extract actual process and step IDs from the map data - using format like PROC_73_L3_MFGP1W3H_CKX0ZC
                  process_id: processId,
                  process_step_id: stepId,
                  processId: processId,
                  stepId: stepId,
                };
              });
            }

            // Extract from elements.processes (updated data structure)
            else if (
              mapData?.elements?.processes &&
              Array.isArray(mapData.elements.processes)
            ) {
              console.log(
                `🔍 Found ${mapData.elements.processes.length} processes in map data`
              );
              console.log(
                "📝 First 3 processes:",
                mapData.elements.processes.slice(0, 3).map((proc: any) => ({
                  id: proc.id,
                  step_name: proc.step_name,
                }))
              );

              steps = mapData.elements.processes.map(
                (process: any, index: number) => {
                  const processId = process.process_id || map.process_id;
                  const stepId = `${processId}_STEP_${String(index + 1).padStart(2, "0")}`;

                  return {
                    id: `${map.id}-step-${index}`,
                    name: process.step_name || `Step ${index + 1}`,
                    description:
                      process.description || `Process step: ${process.step_name}`,
                    order: allSteps.length + index + 1,
                    risk_level: process.risk_level || "Medium",
                    controls: [],
                    category: "Process Step",
                    // Extract actual process and step IDs from the process data - using format like PROC_73_L3_MFGP1W3H_CKX0ZC
                    process_id: processId,
                    process_step_id: stepId,
                    processId: processId,
                    stepId: stepId,
                  };
                }
              );
            }

            // Extract from direct elements array
            else if (mapData?.elements && Array.isArray(mapData.elements)) {
              console.log(
                `🔍 Found ${mapData.elements.length} elements in map data`
              );
              const processElements = mapData.elements.filter(
                (element: any) =>
                  element.step_name && element.step_name.trim() !== ""
              );

              steps = processElements.map((element: any, index: number) => {
                const processId = element.process_id || map.process_id;
                const stepId = `${processId}_STEP_${String(index + 1).padStart(2, "0")}`;

                return {
                  id: `${map.id}-step-${index}`,
                  name: element.step_name,
                  description:
                    element.description || `Process step: ${element.step_name}`,
                  order: allSteps.length + index + 1,
                  risk_level: element.risk_level || "Medium",
                  controls: [],
                  category: "Process Step",
                  // Extract actual process and step IDs from the element data - using format like PROC_73_L3_MFGP1W3H_CKX0ZC
                  process_id: processId,
                  process_step_id: stepId,
                  processId: processId,
                  stepId: stepId,
                };
              });
            }

            if (steps.length > 0) {
             
              allSteps = allSteps.concat(steps);
            } else {
              console.log("❌ No valid process steps found in map data");
              console.log("📊 Map data structure:", {
                hasNodes: !!mapData?.nodes,
                hasElements: !!mapData?.elements,
                hasProcesses: !!mapData?.elements?.processes,
                keys: Object.keys(mapData || {}),
              });
            }
          }
        });

        if (allSteps.length > 0) {
          setProcessSteps(allSteps);
          setProcessStepsLoaded(true);
          return;
        }
      }

      // If we reach here, no process steps were found
      console.log("❌ No process steps found in flowchart data");
      throw new Error("No process steps found in project flowcharts");
    } catch (error) {
      console.log("❌ Failed to load process steps:", error);
    }

    // No data found from any API endpoint
    console.log("❌ No process steps found from any API endpoint");
    setProcessSteps([]);
    setProcessStepsLoaded(false);
    setError(
      "Unable to load process steps from project. Please ensure your project has process maps with defined steps."
    );
  }, [currentProject]);

  // Handle refresh from Risk Assessment Table
  const handleRiskAssessmentRefresh = useCallback(async () => {
    if (!currentProject?.id) return;

    console.log("🔄 Refreshing process steps from Risk Assessment Table");
    setLoading(true);

    try {
      // Clear existing data first
      setProcessSteps([]);
      setProcessStepsLoaded(false);

      // Re-fetch fresh process steps from API
      await loadProcessStepsFromFlowMap();

      console.log("✅ Process steps refreshed successfully");
    } catch (error) {
      console.error("❌ Error refreshing process steps:", error);
      setError("Failed to refresh process steps. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [currentProject, loadProcessStepsFromFlowMap]);

  const loadTabData = useCallback(async () => {
    if (!currentProject?.id) return;

    try {
      switch (activeTab) {
        case "Risk & Control Mapping":
        case "Risk Assessment Table":
          await loadProcessStepsFromFlowMap();
          break;

        case "Controls Overview":
          setLoadingControls(true);
          try {
            const response = await api.get(`/api/process-owner-dashboard/controls/${currentProject.id}`);
            console.log('Controls response:', response.data);
            setControls(response.data.controls || []);
          } catch (error) {
            console.error('Error loading controls:', error);
            setControls([]);
          } finally {
            setLoadingControls(false);
          }
          break;

        case "Analytics":
          // Load process steps for AI analytics
          await loadProcessStepsFromFlowMap();
          const analyticsResponse = await riskMappingAPI.getAnalytics(
            currentProject.id
          );
          // Map the response to match our RiskMetrics interface
          setAnalyticsData({
            overall_risk_score: analyticsResponse.data.overall_risk_score || 0,
            control_coverage: analyticsResponse.data.control_coverage || 0,
            automation_ratio: analyticsResponse.data.automation_ratio || 0,
            compliance_score: analyticsResponse.data.compliance_score || 0,
            high_risk_processes:
              analyticsResponse.data.detailed_metrics?.high_risk_processes || 0,
            critical_findings:
              analyticsResponse.data.detailed_metrics?.critical_findings || 0,
            total_processes:
              analyticsResponse.data.detailed_metrics?.total_processes || 0,
          });
          break;

        default:
          await loadProcessStepsFromFlowMap();
          break;
      }
    } catch (error) {
      console.error("Failed to load tab data:", error);
    }
  }, [activeTab, currentProject, loadProcessStepsFromFlowMap]);

  // Initial data load - reset state when project changes
  useEffect(() => {
    if (currentProject?.id) {
      console.log("🔄 Project changed, resetting process steps state");
      setProcessSteps([]);
      setProcessStepsLoaded(false);
      setError(null);
      loadProcessStepsFromFlowMap();
    } else {
      console.log("❌ No project selected, clearing process steps");
      setProcessSteps([]);
      setProcessStepsLoaded(false);
      setError(null);
    }
  }, [currentProject?.id, loadProcessStepsFromFlowMap]);

  useEffect(() => {
    if (currentProject?.id) {
      loadTabData();
    }
  }, [activeTab, currentProject?.id, loadTabData]);

  // Missing function implementations
  const handleGenerateAI = async () => {
    if (!currentProject?.id) return;

    setLoading(true);
    setGenerating(true);
    setGenerationProgress("Initializing AI generation...");

    try {
      console.log("🤖 Starting AI generation for project:", currentProject.id);

      setGenerationProgress("Analyzing process steps...");

      // Use the correct AI generation endpoint
      const response = await api.post(
        `/api/risk-control-mapping/project/${currentProject.id}/generate-ai`,
        {
          generateRisks: true,
          generateControls: true,
        }
      );

      console.log("✅ AI generation complete:", response.data);

      if (response.data.success) {
        // Clear any previous errors
        setError(null);
        setGenerationProgress("Loading generated data...");

        // Reload mapping data to show the generated results
        await loadMappingData();

        // Show success message
        const risksCount = response.data.summary?.totalRisks || 0;
        const controlsCount = response.data.summary?.totalControls || 0;
        setGenerationProgress(
          `✅ Success! Generated ${risksCount} risks and ${controlsCount} controls`
        );

        console.log(
          `AI generated ${risksCount} risks and ${controlsCount} controls`
        );

        // Clear progress after a delay
        setTimeout(() => {
          setGenerationProgress("");
        }, 3000);
      } else {
        throw new Error(response.data.message || "AI generation failed");
      }
    } catch (error: any) {
      console.error("❌ Failed to generate AI analysis:", error);
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "Failed to generate AI analysis";
      setError(`AI Generation Error: ${errorMessage}`);
      setGenerationProgress("");

      // If it's a 404 error, provide specific guidance
      if (error.response?.status === 404) {
        setError(
          "AI Generation endpoint not found. Please ensure the backend server is running and the API routes are properly configured."
        );
      }
      // If it's a 500 error, suggest checking backend logs
      else if (error.response?.status === 500) {
        setError(
          "Server error during AI generation. Please check backend logs and try again."
        );
      }
    } finally {
      setLoading(false);
      setGenerating(false);
    }
  };

  const loadMappingData = async () => {
    if (!currentProject?.id) return;

    setLoading(true);
    try {
      console.log(
        "🔄 Loading risk mapping data for project:",
        currentProject.id
      );

      // Call the API to get comprehensive risk & control mapping
      const response =
        await ProcessStepRiskControlService.getComprehensiveMapping(
          currentProject.id
        );

      if (response && response.processStepMappings) {
        console.log("✅ Successfully loaded risk mapping data:", response);
        setMappingData(response.processStepMappings);

        // Use project stats from the API response if available
        if (response.projectStats) {
          setProjectStats(response.projectStats);
        } else {
          // Fallback: Calculate project stats from the loaded data
          const totalSteps = response.processStepMappings.length;
          const completeSteps = response.processStepMappings.filter(
            (item: any) => item.summary?.completionStatus === "COMPLETE"
          ).length;

          const totalRisks = response.processStepMappings.reduce(
            (sum: number, item: any) => sum + (item.summary?.totalRisks || 0),
            0
          );

          const totalControls = response.processStepMappings.reduce(
            (sum: number, item: any) =>
              sum + (item.summary?.totalControls || 0),
            0
          );

          const totalMappings = response.processStepMappings.reduce(
            (sum: number, item: any) =>
              sum + (item.summary?.totalMappings || 0),
            0
          );

          setProjectStats({
            totalProcessSteps: totalSteps,
            totalRisks: totalRisks,
            totalControls: totalControls,
            totalMappings: totalMappings,
            completeSteps: completeSteps,
            incompleteSteps: totalSteps - completeSteps,
          });
        }
      } else {
        console.log("❌ No risk mapping data found");
        setMappingData([]);
        setProjectStats({
          totalProcessSteps: processSteps.length || 0,
          totalRisks: 0,
          totalControls: 0,
          totalMappings: 0,
          completeSteps: 0,
          incompleteSteps: processSteps.length || 0,
        });
      }

      // Clear any previous errors
      setError(null);
    } catch (error) {
      console.error("❌ Failed to load risk mapping data:", error);
      // Fallback to empty state but log the actual error
      setMappingData([]);
      setProjectStats({
        totalProcessSteps: processSteps.length || 0,
        totalRisks: 0,
        totalControls: 0,
        totalMappings: 0,
        completeSteps: 0,
        incompleteSteps: processSteps.length || 0,
      });
      setError(
        "Failed to load risk mapping data. Please try refreshing or check if the API is available."
      );
    } finally {
      setLoading(false);
    }
  };

  const handleSaveMappings = async () => {
    if (!currentProject?.id) return;

    setSavingMapping(true);
    try {
      await api.post(`/api/projects/${currentProject.id}/risk-mappings`, {
        mappings: mappingData,
      });
      console.log("Mappings saved successfully");
    } catch (error) {
      console.error("Failed to save mappings:", error);
      setError("Failed to save mappings");
    } finally {
      setSavingMapping(false);
    }
  };

  const handleExportMappingExcel = async () => {
    if (!currentProject?.id) return;

    try {
      const response = await api.get(
        `/api/risk-mapping/${currentProject.id}/export?format=excel`,
        { responseType: 'blob' }
      );

      // Create blob URL and download
      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `risk-assessment-${currentProject.id}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      console.log("Export to Excel successful");
    } catch (error) {
      console.error("Failed to export to Excel:", error);
      setError("Failed to export to Excel");
    }
  };

  const handleControlTesting = async (controlId: string, testData: any) => {
    try {
      console.log('Recording control test for control:', controlId);

      await api.post(`/api/process-owner-dashboard/controls/${controlId}/test`, {
        test_type: testData.test_type || 'manual',
        result: testData.result || 'Effective',
        effectiveness_score: testData.effectiveness_score || 85,
        findings: testData.findings || '',
        test_date: testData.test_date || new Date().toISOString()
      });

      await loadTabData();
      setTestingDialogOpen(false);
      toastService.success('Control test recorded successfully');

    } catch (error) {
      console.error('Error recording control test:', error);
      toastService.error('Failed to record control test');
    }
  };

  const handleGapCreation = async (gapData: any) => {
    try {
      console.log('Creating gap for data:', gapData);

      await api.post('/api/process-owner-dashboard/gaps', {
        control_id: gapData.control_id || selectedControl?.id,
        gap_type: gapData.gap_type || 'Operating Gap',
        description: gapData.description || '',
        severity: gapData.severity || 'Medium',
        remediation_plan: gapData.remediation_plan || '',
        target_resolution_date: gapData.target_resolution_date || '',
        assigned_to: gapData.assigned_to || ''
      });

      await loadTabData();
      setGapDialogOpen(false);
      toastService.success('Control gap recorded successfully');

    } catch (error) {
      console.error('Error creating gap:', error);
      toastService.error('Failed to record control gap');
    }
  };

  const handleAddControl = async (controlData: any) => {
    try {
      console.log('Creating new control:', controlData);
      console.log('Current project ID:', currentProject?.id);

      if (!currentProject?.id) {
        throw new Error('Project ID is required');
      }

      await api.post('/api/process-step-controls', {
        ...controlData,
        project_id: parseInt(currentProject.id.toString()),
        control_id: controlData.control_id || `CTRL_${Date.now()}`,
        effectiveness_score: controlData.operating_effectiveness_score || 85,
        control_effectiveness: controlData.operating_effectiveness || 'Effective',
        control_nature: controlData.automation_level || 'Manual'
      });

      await loadTabData();
      setAddControlModalOpen(false);
      toastService.success('Control added successfully');

    } catch (error) {
      console.error('Error adding control:', error);
      toastService.error(error instanceof Error ? error.message : 'Failed to add control');
    }
  };

  const exportAssessment = async (format?: "json" | "pdf" | "excel") => {
    if (!currentProject?.id || !format) return;

    try {
      if (format === "excel") {
        await handleExportMappingExcel();
      }
      console.log(`Export to ${format} successful`);
    } catch (error) {
      console.error(`Failed to export to ${format}:`, error);
      setError(`Failed to export to ${format}`);
    }
  };

  if (!currentProject) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-gray-500">
          Please select a project to view risk mapping
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-super-dark-purple">Comprehensive Risk Assessment</h1>
          <p className="text-gray-600">Comprehensive analysis of risks, controls, and compliance for{" "}
              {currentProject.name}</p>
        </div>
      </div>

      {/* Tabs Container */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        {/* Tabs */}
        <div className="mt-6 border-b border-gray-200">
          <nav className="flex space-x-8">
            {tabs.map((tab) => (
              <button
                key={tab.name}
                onClick={() => setActiveTab(tab.name)}
                className={`
                  pb-3 px-1 border-b-2 font-medium text-sm transition-colors flex items-center
                  ${
                    activeTab === tab.name
                      ? "border-blue-500 text-blue-600"
                      : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                  }
                `}
              >
                <tab.icon className="w-4 h-4 mr-2" />
                {tab.name}
              </button>
            ))}
          </nav>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">{error}</p>
        </div>
      )}

      {/* Tab Content */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </div>
        ) : (
          <>
            {/* Risk & Control Mapping Tab - MOVED TO TOP */}
            {activeTab === "Risk & Control Mapping" && (
              <div className="space-y-6">
                {/* Progress Indicator */}
                {generationProgress && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <div className="flex items-center gap-3">
                      <Brain className="h-5 w-5 text-blue-600 animate-spin" />
                      <div>
                        <h4 className="font-medium text-blue-800">
                          AI Generation Progress
                        </h4>
                        <p className="text-sm text-blue-700">
                          {generationProgress}
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Debug Info */}
                {error && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                    <h4 className="font-medium text-red-800 mb-2">
                      Error Information:
                    </h4>
                    <p className="text-sm text-red-700 mb-2">
                      Current Project: {currentProject?.name} (ID:{" "}
                      {currentProject?.id})
                    </p>
                    <p className="text-sm text-red-700 mb-2">
                      Process Steps Loaded: {processStepsLoaded ? "Yes" : "No"}
                    </p>
                    <p className="text-sm text-red-700 mb-2">
                      Process Steps Count: {processSteps.length}
                    </p>
                    <p className="text-sm text-red-700 mb-2">
                      Mapping Data Count: {mappingData.length}
                    </p>
                    <p className="text-sm text-red-700">Error: {error}</p>
                  </div>
                )}

                {/* Header Controls */}
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-semibold text-[#221551]">
                      Comprehensive Risk & Control Mapping
                    </h3>
                    <p className="text-sm text-gray-600 mt-1">
                      Complete data mapping for Excel export - all process steps
                      with risks and controls
                    </p>
                  </div>
                  <div className="flex items-center gap-3">
                    <button
                      onClick={() => setShowMappingStats(!showMappingStats)}
                      className="flex items-center gap-2 px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50"
                    >
                      <BarChart3 className="h-4 w-4" />
                      {showMappingStats ? "Hide Stats" : "Show Stats"}
                    </button>
                    <button
                      onClick={() => setShowMappingFilters(!showMappingFilters)}
                      className="flex items-center gap-2 px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50"
                    >
                      <AlertTriangle className="h-4 w-4" />
                      Filters
                    </button>
                    <button
                      onClick={handleGenerateAI}
                      disabled={loading || generating}
                      className="flex items-center gap-2 px-4 py-2 text-sm bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 transition-all"
                    >
                      <Brain
                        className={`h-4 w-4 ${
                          generating ? "animate-spin" : ""
                        }`}
                      />
                      {generating ? "Generating AI..." : "Generate AI"}
                    </button>
                    <button
                      onClick={loadMappingData}
                      disabled={loading}
                      className="flex items-center gap-2 px-4 py-2 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 disabled:opacity-50"
                    >
                      <RefreshCw
                        className={`h-4 w-4 ${loading ? "animate-spin" : ""}`}
                      />
                      Refresh
                    </button>
                    <button
                      onClick={handleSaveMappings}
                      disabled={savingMapping}
                      className="flex items-center gap-2 px-4 py-2 text-sm bg-[#57C8F4] text-white rounded-lg hover:bg-[#30A8CF] disabled:opacity-50"
                    >
                      <Download className="h-4 w-4" />
                      {savingMapping ? "Saving..." : "Save All Changes"}
                    </button>
                    <button
                      onClick={handleExportMappingExcel}
                      className="flex items-center gap-2 px-4 py-2 text-sm bg-[#221551] text-white rounded-lg hover:bg-[#454F83]"
                    >
                      <Download className="h-4 w-4" />
                      Export to Excel
                    </button>
                  </div>
                </div>

                {showMappingStats && projectStats && (
                  <MappingStats stats={projectStats} />
                )}

                {showMappingFilters && (
                  <MappingFilters
                    filters={mappingFilters}
                    onFiltersChange={setMappingFilters}
                    mappingData={mappingData}
                  />
                )}

                <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                  {!loading && processSteps.length === 0 ? (
                    /* Empty State */
                    <div className="text-center py-12">
                      <Users className="w-16 h-16 mx-auto mb-4 text-gray-400" />
                      <h3 className="text-lg font-medium text-gray-900 mb-2">
                        AI-Powered Risk & Control Mapping
                      </h3>
                      <p className="text-gray-600 mb-6 max-w-lg mx-auto">
                        No process steps found. Please create process steps from
                        your Process Maps first, then use AI to automatically
                        generate comprehensive risk mappings with regulatory
                        references and control recommendations.
                      </p>
                      <div className="flex flex-col items-center gap-4">
                        <div className="flex items-center gap-4">
                          <button
                            onClick={loadProcessStepsFromFlowMap}
                            className="flex items-center gap-2 px-6 py-3 bg-[#57C8F4] text-white rounded-lg hover:bg-[#30A8CF] transition-colors"
                          >
                            <RefreshCw className="h-5 w-5" />
                            Try Load Again
                          </button>
                          <button
                            onClick={() => {
                              // Navigate to Process Maps
                              window.location.href = "/app/process-maps";
                            }}
                            className="flex items-center gap-2 px-6 py-3 bg-[#221551] text-white rounded-lg hover:bg-[#454F83] transition-colors"
                          >
                            <Target className="h-5 w-5" />
                            Create Process Maps
                          </button>
                        </div>
                        <p className="text-sm text-gray-500">
                          Current Project:{" "}
                          {currentProject?.name || "No project selected"}
                          {currentProject?.id && ` (ID: ${currentProject.id})`}
                        </p>
                      </div>
                    </div>
                  ) : !loading &&
                    processSteps.length > 0 &&
                    mappingData.length === 0 ? (
                    /* Process Steps Found but No Mapping Data */
                    <div className="text-center py-12">
                      <Brain className="w-16 h-16 mx-auto mb-4 text-blue-500" />
                      <h3 className="text-lg font-medium text-gray-900 mb-2">
                        Process Steps Ready for Risk Mapping
                      </h3>
                      <p className="text-gray-600 mb-6 max-w-lg mx-auto">
                        Found {processSteps.length} process steps. Click
                        "Generate AI" to create comprehensive risk and control
                        mappings.
                      </p>
                      <div className="flex flex-col items-center gap-4">
                        <button
                          onClick={handleGenerateAI}
                          disabled={loading}
                          className="flex items-center gap-2 px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 transition-colors"
                        >
                          <Brain
                            className={`h-5 w-5 ${
                              loading ? "animate-spin" : ""
                            }`}
                          />
                          {loading
                            ? "Generating AI..."
                            : "Generate AI Risk Mapping"}
                        </button>
                      </div>
                    </div>
                  ) : loading ? (
                    <div className="flex items-center justify-center py-12">
                      <RefreshCw className="w-8 h-8 animate-spin text-[#57C8F4]" />
                      <span className="ml-3 text-gray-600">
                        Loading process steps and risk mappings...
                      </span>
                    </div>
                  ) : (
                    /* Main Mapping Table Component */
                    <EnhancedRiskControlMappingTable
                      mappingData={mappingData}
                      onMappingChange={(updatedData) => {
                        setMappingData(updatedData);
                      }}
                      projectId={currentProject?.id?.toString() || ""}
                    />
                  )}
                </div>
              </div>
            )}

            {/* NEW: Risk Assessment Table Tab */}
            {activeTab === "Risk Assessment Table" && (
              <div className="space-y-6">
                <RiskAssessmentTable
                  projectId={currentProject?.id || 0}
                  processSteps={processSteps}
                  mappingData={mappingData}
                  onExport={exportAssessment}
                  onRefresh={handleRiskAssessmentRefresh}
                  error={error}
                />
              </div>
            )}

            {/* Controls Overview Tab */}
            {activeTab === "Controls Overview" && (
              <div className="space-y-6">
                <div className="flex justify-between items-center mb-4">
                  <h2 className="text-2xl font-bold text-gray-900">Controls Overview</h2>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setAddControlModalOpen(true)}
                      className="flex items-center px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 cursor-pointer transition-colors"
                      title="Add new control"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Add Control
                    </button>
                    <button
                      onClick={loadTabData}
                      className="p-2 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                    >
                      <RefreshCw className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                {loadingControls ? (
                  <div className="flex items-center justify-center py-12">
                    <RefreshCw className="w-8 h-8 text-blue-600 animate-spin" />
                    <span className="ml-3 text-gray-600">Loading controls...</span>
                  </div>
                ) : (
                  <ControlsOverview
                    controls={controls}
                    onEditControl={(control) => {
                      setSelectedControl(control);
                      setDetailsOpen(true);
                    }}
                    onTestControl={(control) => {
                      setSelectedControl(control);
                      setTestingDialogOpen(true);
                    }}
                  />
                )}
              </div>
            )}

            {/* Residual Risk Calculator Tab */}
            {activeTab === "Residual Risk Calculator" && (
              <div className="space-y-4">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <h3 className="text-lg font-semibold text-blue-900 mb-2">How to Use Residual Risk Calculator</h3>
                  <ol className="list-decimal list-inside text-sm text-blue-800 space-y-1">
                    <li>Select a risk from the table below</li>
                    <li>The calculator will load the risk details and associated controls</li>
                    <li>Click "Calculate Residual Risk" to perform AI-enhanced calculation</li>
                    <li>Optionally apply manual override if needed</li>
                  </ol>
                </div>

                {selectedRiskForCalculation ? (
                  <ResidualRiskCalculator
                    riskId={selectedRiskForCalculation}
                    onCalculationComplete={(result) => {
                      console.log("Residual risk calculation complete:", result);
                    }}
                    allowManualOverride={true}
                  />
                ) : (
                  <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Available Risks for Calculation</h3>
                    <RiskAssessmentTable
                      projectId={currentProject?.id || 0}
                      processSteps={processSteps}
                      mappingData={mappingData}
                      onExport={exportAssessment}
                      onRefresh={handleRiskAssessmentRefresh}
                      error={error}
                      onRiskSelect={(riskId: number) => {
                        console.log("Risk selected for calculation:", riskId);
                        setSelectedRiskForCalculation(riskId);
                      }}
                    />
                  </div>
                )}

                {selectedRiskForCalculation && (
                  <div className="flex justify-center">
                    <button
                      onClick={() => setSelectedRiskForCalculation(null)}
                      className="px-6 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 transition-colors"
                    >
                      ← Back to Risk Selection
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* Template Quality Check Tab */}
            {activeTab === "Template Quality Check" && (
              <div className="space-y-6">
                <RiskTemplateQCTab />
              </div>
            )}

            {activeTab === "Template Import" && (
              <div className="space-y-6">
                <TemplateImportTab />
              </div>
            )}

            {/* Database-Driven Risk Taxonomy & Classification Panel - MOVED BELOW */}
            {activeTab === "Risk & Control Mapping" &&
              databaseCategories.length > 0 && (
                <div className="mt-6">
                  <h3 className="text-lg font-semibold mb-4 flex items-center">
                    <Shield className="w-5 h-5 mr-2 text-electric-blue" />
                    Enhanced Risk Taxonomy & AI Classification
                  </h3>
                  <div className="bg-light-purple rounded-lg p-4">
                    <div className="flex justify-between items-center mb-4">
                      <h4 className="font-medium text-super-dark-purple">
                        Database Risk Categories ({databaseCategories.length})
                      </h4>
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => setShowClassificationPanel(true)}
                          className="text-sm bg-electric-blue text-white px-3 py-1 rounded hover:bg-dusky-purple transition-colors flex items-center space-x-1"
                        >
                          <Brain size={14} />
                          <span>AI Classify</span>
                        </button>
                        <button className="text-sm text-electric-blue hover:underline">
                          Regulations
                        </button>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                      {databaseCategories
                        .slice(0, 12)
                        .map((category: any) => (
                          <div
                            key={category.id}
                            className="bg-white rounded p-3 border border-gray-200 hover:border-electric-blue transition-colors cursor-pointer"
                            onClick={() => {
                              setSelectedProcessStep(category.name);
                              setShowClassificationPanel(true);
                            }}
                          >
                            <div className="flex items-center justify-between mb-2">
                              <h5 className="font-medium text-super-dark-purple text-sm">
                                {category.name}
                              </h5>
                              <span
                                className={`px-2 py-1 rounded text-xs font-medium ${category.color_code}`}
                              >
                                {category.level}
                              </span>
                            </div>
                            <div className="text-xs text-gray-500 mb-2">
                              {category.level_1} → {category.level_2}
                            </div>
                            <p className="text-xs text-gray-600 mb-2 line-clamp-2">
                              {category.description}
                            </p>

                            {/* Framework Mappings */}
                            {category.framework_mapping &&
                              Object.keys(category.framework_mapping).length >
                                0 && (
                                <div className="mb-2">
                                  <div className="flex flex-wrap gap-1">
                                    {Object.keys(category.framework_mapping)
                                      .slice(0, 3)
                                      .map((framework) => (
                                        <span
                                          key={framework}
                                          className="px-1 py-0.5 bg-electric-blue text-white text-xs rounded"
                                        >
                                          {framework.toUpperCase()}
                                        </span>
                                      ))}
                                  </div>
                                </div>
                              )}

                            {/* Tags */}
                            {category.tags && category.tags.length > 0 && (
                              <div className="mb-2">
                                <div className="flex flex-wrap gap-1">
                                  {category.tags
                                    .slice(0, 3)
                                    .map((tag: string, tagIndex: number) => (
                                      <span
                                        key={tagIndex}
                                        className="px-1 py-0.5 bg-light-aqua text-super-dark-purple text-xs rounded flex items-center space-x-1"
                                      >
                                        <Tag size={8} />
                                        <span>{tag}</span>
                                      </span>
                                    ))}
                                  {category.tags.length > 3 && (
                                    <span className="text-xs text-gray-500">
                                      +{category.tags.length - 3} more
                                    </span>
                                  )}
                                </div>
                              </div>
                            )}

                            {/* AI Suggested Tags */}
                            {category.ai_suggested_tags &&
                              category.ai_suggested_tags.length > 0 && (
                                <div>
                                  <div className="flex flex-wrap gap-1">
                                    {category.ai_suggested_tags
                                      .slice(0, 2)
                                      .map((aiTag: any, aiIndex: number) => (
                                        <span
                                          key={aiIndex}
                                          className="px-1 py-0.5 bg-purple-100 text-purple-800 text-xs rounded flex items-center space-x-1"
                                        >
                                          <Brain size={8} />
                                          <span>{aiTag.name}</span>
                                          <span className="text-purple-600">
                                            (
                                            {Math.round(aiTag.confidence * 100)}
                                            %)
                                          </span>
                                        </span>
                                      ))}
                                  </div>
                                </div>
                              )}
                          </div>
                        ))}
                    </div>

                    {databaseCategories.length > 12 && (
                      <div className="mt-4 text-center">
                        <button className="text-sm text-electric-blue hover:underline">
                          View All {databaseCategories.length} Categories
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              )}

            {/* Analytics Tab */}
            {activeTab === "Analytics" && (
              <AIAnalyticsRecommendations
                projectId={currentProject?.id || 0}
                processSteps={processSteps}
                riskAssessments={[]}
                existingData={analyticsInsights}
                viewMode={viewMode}
                onAnalysisComplete={(data) => {
                  console.log("Analytics complete:", data);
                  setAnalyticsInsights(data);
                }}
                viewType="analytics"
              />
            )} 

            {/* COMMENTED OUT - Database-Driven Risk Taxonomy Section - Can be uncommented if needed in future */}
            {/* {activeTab === 'Risk & Control Mapping' && databaseCategories.length > 0 && (
              <div className="mt-6">
                <h3 className="text-lg font-semibold mb-4 flex items-center">
                  <Shield className="w-5 h-5 mr-2 text-electric-blue" />
                  Enhanced Risk Taxonomy & AI Classification
                </h3>
                <div className="bg-light-purple rounded-lg p-4">
                  <div className="flex justify-between items-center mb-4">
                    <h4 className="font-medium text-super-dark-purple">Database Risk Categories ({databaseCategories.length})</h4>
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => setShowClassificationPanel(true)}
                        className="text-sm bg-electric-blue text-white px-3 py-1 rounded hover:bg-dusky-purple transition-colors flex items-center space-x-1"
                      >
                        <Brain size={14} />
                        <span>AI Classify</span>
                      </button>
                      <button className="text-sm text-electric-blue hover:underline">Regulations</button>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                    {databaseCategories.slice(0, 12).map((category: any, index: number) => (
                      <div key={category.id} className="bg-white rounded p-3 border border-gray-200 hover:border-electric-blue transition-colors cursor-pointer"
                           onClick={() => {
                             setSelectedProcessStep(category.name);
                             setShowClassificationPanel(true);
                           }}>
                        <div className="flex items-center justify-between mb-2">
                          <h5 className="font-medium text-super-dark-purple text-sm">{category.name}</h5>
                          <span className={`px-2 py-1 rounded text-xs font-medium ${category.color_code}`}>
                            {category.level}
                          </span>
                        </div>
                        <div className="text-xs text-gray-500 mb-2">
                          {category.level_1} → {category.level_2}
                        </div>
                        <p className="text-xs text-gray-600 mb-2 line-clamp-2">{category.description}</p>

                        {category.framework_mapping && Object.keys(category.framework_mapping).length > 0 && (
                          <div className="mb-2">
                            <div className="flex flex-wrap gap-1">
                              {Object.keys(category.framework_mapping).slice(0, 3).map(framework => (
                                <span key={framework} className="px-1 py-0.5 bg-electric-blue text-white text-xs rounded">
                                  {framework.toUpperCase()}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}

                        {category.tags && category.tags.length > 0 && (
                          <div className="mb-2">
                            <div className="flex flex-wrap gap-1">
                              {category.tags.slice(0, 3).map((tag: string, tagIndex: number) => (
                                <span key={tagIndex} className="px-1 py-0.5 bg-light-aqua text-super-dark-purple text-xs rounded flex items-center space-x-1">
                                  <Tag size={8} />
                                  <span>{tag}</span>
                                </span>
                              ))}
                              {category.tags.length > 3 && (
                                <span className="text-xs text-gray-500">+{category.tags.length - 3} more</span>
                              )}
                            </div>
                          </div>
                        )}

                        {category.ai_suggested_tags && category.ai_suggested_tags.length > 0 && (
                          <div>
                            <div className="flex flex-wrap gap-1">
                              {category.ai_suggested_tags.slice(0, 2).map((aiTag: any, aiIndex: number) => (
                                <span key={aiIndex} className="px-1 py-0.5 bg-purple-100 text-purple-800 text-xs rounded flex items-center space-x-1">
                                  <Brain size={8} />
                                  <span>{aiTag.name}</span>
                                  <span className="text-purple-600">({Math.round(aiTag.confidence * 100)}%)</span>
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>

                  {databaseCategories.length > 12 && (
                    <div className="mt-4 text-center">
                      <button className="text-sm text-electric-blue hover:underline">
                        View All {databaseCategories.length} Categories
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )} */}
          </>
        )}
      </div>

      {/* Add Control Modal */}
      <AddControlModal
        isOpen={addControlModalOpen}
        onClose={() => setAddControlModalOpen(false)}
        onSave={handleAddControl}
      />

      {/* Control Details/Edit Modal */}
      {detailsOpen && selectedControl && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold text-gray-900">Edit Control</h2>
              <button
                onClick={() => setDetailsOpen(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <XCircle className="w-5 h-5" />
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Control Name</label>
                <input
                  type="text"
                  id="edit-control-name"
                  defaultValue={selectedControl.control_name}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Control Type</label>
                <select
                  id="edit-control-type"
                  defaultValue={selectedControl.control_type}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="Preventive">Preventive</option>
                  <option value="Detective">Detective</option>
                  <option value="Corrective">Corrective</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Testing Frequency</label>
                <select
                  id="edit-testing-frequency"
                  defaultValue={selectedControl.testing_frequency}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="Daily">Daily</option>
                  <option value="Weekly">Weekly</option>
                  <option value="Monthly">Monthly</option>
                  <option value="Quarterly">Quarterly</option>
                  <option value="Semi-Annually">Semi-Annually</option>
                  <option value="Annually">Annually</option>
                </select>
              </div>
            </div>
            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => setDetailsOpen(false)}
                className="px-4 py-2 text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  try {
                    const controlName = (document.getElementById('edit-control-name') as HTMLInputElement)?.value;
                    const controlType = (document.getElementById('edit-control-type') as HTMLSelectElement)?.value;
                    const testingFrequency = (document.getElementById('edit-testing-frequency') as HTMLSelectElement)?.value;

                    await api.put(`/api/process-step-controls/${selectedControl.id}`, {
                      control_name: controlName,
                      control_type: controlType,
                      testing_frequency: testingFrequency
                    });

                    setDetailsOpen(false);
                    await loadTabData();
                    toastService.success('Control updated successfully');
                  } catch (error) {
                    console.error('Error updating control:', error);
                    toastService.error('Failed to update control');
                  }
                }}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Control Testing Modal */}
      {testingDialogOpen && selectedControl && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold text-gray-900">Record Control Test</h2>
              <button
                onClick={() => setTestingDialogOpen(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <XCircle className="w-5 h-5" />
              </button>
            </div>
            <div className="mb-4">
              <p className="text-sm text-gray-600">
                Recording test for: <span className="font-medium">{selectedControl.control_name}</span>
              </p>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Test Result</label>
                <select
                  id="test-result"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="Effective">Effective</option>
                  <option value="Partially Effective">Partially Effective</option>
                  <option value="Ineffective">Ineffective</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Effectiveness Score (%)</label>
                <input
                  type="number"
                  id="effectiveness-score"
                  min="0"
                  max="100"
                  defaultValue="85"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Test Findings</label>
                <textarea
                  id="test-findings"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  rows={3}
                  placeholder="Enter test findings and observations..."
                />
              </div>
            </div>
            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => setTestingDialogOpen(false)}
                className="px-4 py-2 text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  const testResult = (document.getElementById('test-result') as HTMLSelectElement)?.value;
                  const effectivenessScore = (document.getElementById('effectiveness-score') as HTMLInputElement)?.value;
                  const findings = (document.getElementById('test-findings') as HTMLTextAreaElement)?.value;

                  handleControlTesting(selectedControl.id, {
                    test_type: 'observation',
                    result: testResult,
                    effectiveness_score: parseInt(effectivenessScore) || 85,
                    findings: findings || 'Test completed successfully',
                    test_date: new Date().toISOString()
                  });
                }}
                className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
              >
                Record Test
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Control Gap Modal */}
      {gapDialogOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold text-gray-900">Report Control Gap</h2>
              <button
                onClick={() => setGapDialogOpen(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <XCircle className="w-5 h-5" />
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Control *</label>
                <select
                  id="gap-control"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  defaultValue={selectedControl?.id || ''}
                >
                  <option value="">Select a control...</option>
                  {controls.map((control) => (
                    <option key={control.id} value={control.id}>
                      {control.control_name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Gap Type</label>
                <select
                  id="gap-type"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="Operating Gap">Operating Gap</option>
                  <option value="Design Gap">Design Gap</option>
                  <option value="Documentation Gap">Documentation Gap</option>
                  <option value="Training Gap">Training Gap</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Severity</label>
                <select
                  id="gap-severity"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="High">High</option>
                  <option value="Medium">Medium</option>
                  <option value="Low">Low</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  id="gap-description"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  rows={3}
                  placeholder="Describe the identified gap..."
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Remediation Plan</label>
                <textarea
                  id="gap-remediation"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  rows={2}
                  placeholder="Describe the plan to address this gap..."
                />
              </div>
            </div>
            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => setGapDialogOpen(false)}
                className="px-4 py-2 text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  const controlId = (document.getElementById('gap-control') as HTMLSelectElement)?.value;
                  const gapType = (document.getElementById('gap-type') as HTMLSelectElement)?.value;
                  const severity = (document.getElementById('gap-severity') as HTMLSelectElement)?.value;
                  const description = (document.getElementById('gap-description') as HTMLTextAreaElement)?.value;
                  const remediation = (document.getElementById('gap-remediation') as HTMLTextAreaElement)?.value;

                  if (!controlId) {
                    toastService.error('Please select a control');
                    return;
                  }

                  handleGapCreation({
                    control_id: parseInt(controlId),
                    gap_type: gapType,
                    severity: severity,
                    description: description,
                    remediation_plan: remediation,
                    target_resolution_date: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
                    assigned_to: 'Current User'
                  });
                }}
                className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
              >
                Report Gap
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RiskMapping;
